import React, { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { isDiscountExpired, formatDiscountExpiry, formatActaDate, formatActaTime } from './dateHelpers';
import ActaFilesButtons from './ActaFilesButtons';

const DomainActasCard = ({ domain, validActas, allowPartialPayment, selectedActas, onToggleActa }) => {
  const [expanded, setExpanded] = useState(validActas.length <= 1);
  const count = validActas.length;

  const renderActa = (acta, ai) => {
    const isPaid = acta.status === 'paid';
    const isSelected = selectedActas?.includes(acta.number);
    const discExpired = isDiscountExpired(acta.discount_expiry);
    const expiryFormatted = formatDiscountExpiry(acta.discount_expiry);
    const montoExpired = isDiscountExpired(acta.monto_expiry);
    // Quita is expired if: discount_expiry passed, OR no discount_expiry but monto_expiry passed
    const expired = discExpired || (!acta.discount_expiry && montoExpired);
    // Show the quita deadline date inline (use discount_expiry if available, else monto_expiry)
    const quitaExpiryFormatted = formatDiscountExpiry(acta.discount_expiry) || formatDiscountExpiry(acta.monto_expiry);
    // Effective amount for display: monto if available, else monto_quita
    const hasBothAmounts = (acta.monto > 0) && (acta.monto_quita > 0);
    const effectiveAmount = acta.monto > 0 ? acta.monto : (acta.monto_quita || 0);
    return (
      <div key={ai} className={`rounded px-3 py-2 text-sm ${isPaid ? 'bg-emerald-50 opacity-60' : 'bg-amber-50'}`}>
        <div className="flex items-start gap-2">
          {allowPartialPayment && !isPaid && (
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggleActa?.(acta.number)}
              className="mt-0.5"
              data-testid={`acta-check-${acta.number}`}
            />
          )}
          <div className="flex-1">
            <div className="flex justify-between items-start">
              <span className="font-medium text-slate-700">
                Acta {acta.number}
                {isPaid && <span className="ml-1.5 text-xs text-emerald-700 font-semibold">PAGADA</span>}
              </span>
              {(acta.fecha || acta.hora) && (
                <span className="text-xs text-slate-500">
                  {formatActaDate(acta.fecha)}{acta.fecha && acta.hora ? ' ' : ''}{acta.hora ? formatActaTime(acta.hora) + 'hs' : ''}
                </span>
              )}
            </div>
            {acta.infraccion && <p className="text-xs text-slate-600 mt-0.5">{acta.infraccion}</p>}
            {effectiveAmount > 0 && (
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                <span className="text-xs font-medium text-slate-700">${effectiveAmount.toLocaleString('es-AR')}</span>
                {hasBothAmounts && !expired && (
                  <span className="text-xs font-medium text-emerald-700" data-testid={`acta-quita-${acta.number}`}>
                    c/Quita: ${acta.monto_quita.toLocaleString('es-AR')}
                    {quitaExpiryFormatted && <span className="text-amber-600 font-normal ml-1">(vence {quitaExpiryFormatted})</span>}
                  </span>
                )}
                {hasBothAmounts && expired && (
                  <span className="text-xs font-medium text-red-500 line-through" data-testid={`acta-quita-${acta.number}`}>
                    c/Quita: ${acta.monto_quita.toLocaleString('es-AR')}
                    {quitaExpiryFormatted && <span className="font-normal ml-1">(venció {quitaExpiryFormatted})</span>}
                  </span>
                )}
              </div>
            )}
            {expiryFormatted && !hasBothAmounts && (
              <p className={`text-xs mt-0.5 ${expired ? 'text-red-600 font-semibold' : 'text-amber-700'}`} data-testid={`acta-quita-expiry-${acta.number}`}>
                {expired ? `Quita vencida el ${expiryFormatted}` : `Quita vence el ${expiryFormatted}`}
              </p>
            )}
            <ActaFilesButtons acta={acta} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg p-3 border border-amber-100">
      <div className="flex items-center justify-between mb-1">
        <p className="font-mono font-bold text-slate-800 text-base">{domain.plate}</p>
        {count > 0 && (
          <span className="text-xs font-medium bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
            {count} acta{count > 1 ? 's' : ''}
          </span>
        )}
      </div>
      {count > 0 && (
        <>
          {renderActa(validActas[0], 0)}
          {count > 1 && (
            <>
              {expanded && (
                <div className="space-y-1.5 mt-1.5">
                  {validActas.slice(1).map((acta, ai) => renderActa(acta, ai + 1))}
                </div>
              )}
              <button
                onClick={() => setExpanded(!expanded)}
                className="w-full mt-2 text-center text-xs font-medium text-amber-700 hover:text-amber-900 py-1.5 rounded-md hover:bg-amber-100/50 transition-colors"
              >
                {expanded ? 'Ver menos' : `Ver ${count - 1} acta${count - 1 > 1 ? 's' : ''} mas`}
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DomainActasCard;
